import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Text, 
  Button, 
  Group, 
  Stack, 
  Alert, 
  LoadingOverlay,
  Avatar,
  Paper,
  // Divider,
  List,
  ThemeIcon
} from '@mantine/core';
import { 
  IconExternalLink, 
  IconCheck, 
  // IconX, 
  IconInfoCircle,
  IconUser,
  IconLogout,
  IconAlertCircle,
  IconShieldCheck
} from '@tabler/icons-react';
import { lucidAuthService } from '../../../shared/services/lucidAuthService';

const LucidAuthModal = ({ opened, onClose, onAuthSuccess, onAuthError }) => {
  const [authState, setAuthState] = useState('idle'); // idle, authenticating, success, error
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check authentication status on component mount and modal open
  useEffect(() => {
    if (opened) {
      checkAuthStatus();
    }
  }, [opened]);

  const checkAuthStatus = () => {
    const authenticated = lucidAuthService.isAuthenticated();
    const user = lucidAuthService.getUserInfo();
    
    setIsAuthenticated(authenticated);
    setUserInfo(user);
    setError(null);
    
    if (authenticated) {
      setAuthState('success');
    } else {
      setAuthState('idle');
    }
  };

  const handleAuthenticate = async () => {
    try {
      setIsLoading(true);
      setAuthState('authenticating');
      setError(null);

      const result = await lucidAuthService.initiateAuth();
      
      if (result.success) {
        setAuthState('success');
        setIsAuthenticated(true);
        setUserInfo(result.userInfo);
        
        // Call success callback
        if (onAuthSuccess) {
          onAuthSuccess(result);
        }
      }

    } catch (error) {
      console.error('Authentication failed:', error);
      setAuthState('error');
      setError(error.message);
      
      // Call error callback
      if (onAuthError) {
        onAuthError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    lucidAuthService.logout();
    setIsAuthenticated(false);
    setUserInfo(null);
    setAuthState('idle');
    setError(null);
  };

  const handleClose = () => {
    // Reset state when closing
    if (!isAuthenticated) {
      setAuthState('idle');
      setError(null);
    }
    onClose();
  };

  const handleRetry = () => {
    setError(null);
    setAuthState('idle');
  };

  const renderAuthenticationContent = () => {
    switch (authState) {
      case 'idle':
        return (
          <Stack gap="md">
            <Alert icon={<IconInfoCircle size="1rem" />} title="Lucid Charts Authentication Required">
              To export diagrams to Lucid Charts, you need to authenticate with your Lucid account.
              This will allow the application to create documents on your behalf.
            </Alert>

            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text size="sm" fw={500}>What will happen:</Text>
                <List gap="xs" size="sm" center>
                  <List.Item icon={<ThemeIcon size={20} radius="xl" variant="light"><IconExternalLink size="0.8rem" /></ThemeIcon>}>
                    A popup window will open with Lucid's authentication page
                  </List.Item>
                  <List.Item icon={<ThemeIcon size={20} radius="xl" variant="light"><IconShieldCheck size="0.8rem" /></ThemeIcon>}>
                    You'll sign in with your existing Lucid account
                  </List.Item>
                  <List.Item icon={<ThemeIcon size={20} radius="xl" variant="light"><IconCheck size="0.8rem" /></ThemeIcon>}>
                    Grant permission to create documents in your account
                  </List.Item>
                </List>
              </Stack>
            </Paper>

            <Group position="center" mt="md">
              <Button
                onClick={handleAuthenticate}
                loading={isLoading}
                leftSection={<IconExternalLink size="1rem" />}
                size="md"
              >
                Authenticate with Lucid Charts
              </Button>
            </Group>
          </Stack>
        );

      case 'authenticating':
        return (
          <Stack gap="md" align="center">
            <LoadingOverlay visible={true} />
            <Text>Opening Lucid Charts authentication...</Text>
            <Text size="sm" color="dimmed">
              Please complete the authentication in the popup window.
              If you don't see a popup, check if it was blocked by your browser.
            </Text>
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
          </Stack>
        );

      case 'success':
        return (
          <Stack gap="md">
            <Alert icon={<IconCheck size="1rem" />} title="Successfully Authenticated" color="green">
              You're now connected to Lucid Charts and can export diagrams.
            </Alert>

            {userInfo && (
              <Paper p="md" withBorder>
                <Group>
                  <Avatar radius="xl" size="md">
                    <IconUser size="1.2rem" />
                  </Avatar>
                  <div>
                    <Text size="sm" fw={500}>
                      {userInfo.name || userInfo.email || 'Lucid User'}
                    </Text>
                    <Text size="xs" color="dimmed">
                      Connected to Lucid Charts
                    </Text>
                  </div>
                </Group>
              </Paper>
            )}

            <Group position="apart" mt="md">
              <Button variant="subtle" onClick={handleLogout} leftSection={<IconLogout size="1rem" />}>
                Disconnect
              </Button>
              <Button onClick={handleClose}>
                Continue
              </Button>
            </Group>
          </Stack>
        );

      case 'error':
        return (
          <Stack gap="md">
            <Alert icon={<IconAlertCircle size="1rem" />} title="Authentication Failed" color="red">
              {error || 'An error occurred during authentication.'}
            </Alert>

            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text size="sm" fw={500}>Common solutions:</Text>
                <List gap="xs" size="sm">
                  <List.Item>Make sure popups are allowed for this site</List.Item>
                  <List.Item>Check that you have a Lucid Charts account</List.Item>
                  <List.Item>Try refreshing the page and attempting again</List.Item>
                  <List.Item>Ensure your browser allows third-party cookies</List.Item>
                </List>
              </Stack>
            </Paper>

            <Group position="center" mt="md">
              <Button variant="subtle" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleRetry}>
                Try Again
              </Button>
            </Group>
          </Stack>
        );

      default:
        return null;
    }
  };

  const getModalTitle = () => {
    switch (authState) {
      case 'success':
        return 'Lucid Charts Connected';
      case 'error':
        return 'Authentication Error';
      case 'authenticating':
        return 'Authenticating...';
      default:
        return 'Connect to Lucid Charts';
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={getModalTitle()}
      size="md"
      centered
      closeOnClickOutside={authState !== 'authenticating'}
      closeOnEscape={authState !== 'authenticating'}
      withCloseButton={authState !== 'authenticating'}
    >
      <div style={{ position: 'relative', minHeight: '200px' }}>
        {renderAuthenticationContent()}
      </div>
    </Modal>
  );
};

export default LucidAuthModal;